<template>
    <!-- Confirm Password -->
    <div class="row">
        <div class="col-sm-12 mb-3" v-if="header">
            <div class="d-flex flex-column gap-3 text-center align-items-center">
	            <i class="bi w-auto h-auto fs-large bi-person"></i>
                <h3 class="fw-semi-bold m-0">Change Username</h3>
                <p class="text-secondary m-0">Please enter a new username in the text field below.</p>
            </div>
            <div v-if="formErrors.username" class="validation-error mt-0 text-center" v-html="formErrors.username"></div>
        </div>

        <div ref="changeUsernameForm" class="col-12">
	        <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken mt-3 mb-1" :class="formErrors.username ? 'border-danger' : 'border-secondary'">
		        <label for="username">
			        <i class="me-3 dd-icon bi bi-person-lock"></i>
		        </label>
		        <input v-model="username" @input="usernameInputAction" ref="usernameField" type="text" id="usernameChange" name="username" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" placeholder="Enter New Username" autofocus @keyup.enter="saveUsername" >

	        </div>
	        <div class="d-flex justify-content-start align-items-center gap-2 mb-1">
		        <div class="fs-6 d-flex align-items-center">
			        <i style="width: 1rem; height: 1rem; cursor: default" class="fs-6 p-0 me-1 bi " :class="usernameCheckClass"></i>
			        <small style="font-size: 0.75rem;" class="text-muted" v-html="usernameCheckStatusMessage"></small>
		        </div>
		        <div class="fs-6 d-flex align-items-center">
			        <i style="width: 1rem; height: 1rem; cursor: default" class="fs-6 p-0 me-1 bi" :class="{'text-success bi-check-circle' : username && username.length >= 3, 'text-danger bi-x-circle' : !username || username.length < 3, }"></i>
			        <small style="font-size: 0.75rem;" class="text-muted">Minimum 3 character</small>
		        </div>
	        </div>

	        <div class="text-center mt-3"><small class="text-muted">Do not choose a username that is associated with yourself such as your email, address, or real name.</small></div>

	        <div class="d-flex flex-row align-items-center justify-content-center gap-3 text-center ">
                <button class="btn btn-outline-secondary rounded-pill py-2 px-3" @click="close">Cancel</button>
                <button v-if="!autoSubmit" class="btn btn-dark rounded-pill py-2 px-3" @click="saveUsername" :disabled="hasFormErrors || !isUsernameValid">Save Changes</button>
            </div>
        </div>
    </div>

</template>

<script setup>
import { computed, watch, ref, onMounted, nextTick } from "vue";
import apiClient from "@/utils/axios.js";
import {useAppHelpers} from "@/composables/useAppHelpers.js";
import {useHttpErrorManager} from "@/composables/useHttpErrorManager.js";
import {useDebounceFn} from "@vueuse/core";
import { useAuthStore } from "@/stores/auth-store.js";
import { useToast } from "@/composables/useToast.js";

const props = defineProps({
	header: { type: Boolean, required: false, default: true },
	autoSubmit: { type: Boolean, required: false, default: false }
});

const emits = defineEmits(['closeChangeUsername']);

const auth = useAuthStore();
const helper = useAppHelpers();
const { displayToast } = useToast();

const changeUsernameForm = ref(null);

const user = ref(auth.user);
watch(() => auth.user, (newVal) => {
	user.value = newVal;
});

const username = ref(props.username ? props.username : '');
const formErrors = ref({});

const usernameField = ref(null);
onMounted(() => {
	nextTick(() => {
		usernameField.value.focus();
	});
});

const removeFormErrorKey = (key) => {
    delete formErrors.value[key];
};

const hasFormErrors = computed(() => {
    return Object.keys(formErrors.value).length > 0;
});

const usernameAvailable = ref(null);
const checkingUsername = ref(false);

const usernameCheckStatusMessage = computed(() => {
	if(username.value === auth.user.username){
		return 'Your Current Username';
	}
	else if(checkingUsername.value){
		return 'Checking username availability';
	}
	else if (username.value.length >= 3 && usernameAvailable.value !== null && usernameAvailable.value && usernameAvailable.value === true) {
		return 'Username Available';
	}
	else if (username.value.length >= 3 && usernameAvailable.value !== null && usernameAvailable.value && usernameAvailable.value === false) {
		return 'Username Not Available';
	}
	else if(username.value.length < 3 || (username.value.length > 3  && usernameAvailable.value == null)){
		return 'Type Valid Username';
	}
	else if(username.value !== auth.user.username){
		return 'Checking username availability';
	}
});

const usernameCheckClass = computed(() => {
	if(username.value === auth.user.username){
		return 'text-success bi-check-circle';
	}
	else if(checkingUsername.value){
		return 'text-dark bi-arrow-clockwise';
	}
	else if (username.value.length >= 3 && usernameAvailable.value !== null && usernameAvailable.value && usernameAvailable.value === true) {
		return 'text-success bi-check-circle';
	}
	else if (username.value.length >= 3 && usernameAvailable.value !== null && usernameAvailable.value && usernameAvailable.value === false) {
		return 'text-danger bi-x-circle';
	}
	else if(username.value.length < 3 || (username.value.length > 3  && usernameAvailable.value == null)){
		return 'text-dark bi-question-circle';
	}
	else if(!checkingUsername.value && username.value !== auth.user.username){
		return 'text-dark bi-arrow-clockwise';
	}
});

// Debounce the username check function to prevent excessive API calls
const usernameInputAction = () => {
	removeFormErrorKey('username');
	debounceUsernameCheck();
}

const debounceUsernameCheck = useDebounceFn(async () => {
	await checkUsernameAvailability();
}, 500);

// Function to check if the username is available
const checkUsernameAvailability = async () => {
	if (!username.value) {
		usernameAvailable.value = null;
		// formErrors.value.username = 'Please enter a username to continue.';
		return;
	}

	if (username.value.length < 3) {
		usernameAvailable.value = null;
		// formErrors.value.username = 'Username must be at least 3 characters';
		return;
	}

	try {
		checkingUsername.value = true;
		const response = await apiClient.post('/auth/check-username', {
			username: username.value
		});
		checkingUsername.value = false;
		usernameAvailable.value = response.data.available;
		removeFormErrorKey('username');
	} catch (error) {
		checkingUsername.value = false;
		usernameAvailable.value = null;
		formErrors.value.username = error.response.data.message;
	}
};

// Function to validate username
const validateUsername = () => {

	if(username.value === auth.user.username){
		return true;
	}
	if(checkingUsername.value){
		return false;
	}
	if (!username.value) {
		return false;
	}
	if (username.value.length < 3) {
		return false;
	}
	if (usernameAvailable.value !== null && !usernameAvailable.value) {
		return false;
	}
	return true;
};

const isUsernameValid = computed(() => {
	return validateUsername();
});

const saveUsername = async () => {
	if (!validateUsername()) {
		return;
	}
	if(username.value === auth.user.username){
		return;
	}
	try {
		helper.loaderOn(changeUsernameForm.value);
		const response = await apiClient.post('/auth/user/changeUsername', {
			username: username.value
		});
		if (response.status === 200) {
			displayToast('Username Changed Successfully!', 'outline-success', 8000);
			emits('closeChangeUsername');
		}
	} catch (error) {
		helper.loaderOff(changeUsernameForm.value);
		let errorObj = await useHttpErrorManager().handleError(error);
		if (error.response.status === 422) {
			formErrors.value = errorObj;
			removeFormErrorKey('full_message');
			removeFormErrorKey('alt_message');
			removeFormErrorKey('err_message');
		}
	}
};

const close = async () => {
	emits('closeChangeUsername');
}

defineExpose({
    close
});
</script>
